import { Box, DotsLoader } from '@drone-js/core';
import WalletCard from '@v2/components/Wallet/WalletCard';
import { useServiceResolver } from '@services/useServiceResolver';
import { useSetNavHeader } from '@v2/contexts/navigation/hooks/useSetNavHeader';
import React from 'react';
import { usePageBackground } from '../hooks/usePageBackground';
import { useWalletContext } from '../../contexts/wallet/hooks/useWalletContext';
import { useStore } from 'zustand';
import { PinForm, PinFormProps } from '@v2/components/Forms/PinForm/PinForm';
import { WalletOtpForm } from '@v2/components/Forms/WalletOtpForm/WalletOtpForm';
import { usePushNotification, useRenderNotification } from '@components/Notification/useNotification';
import { WithWalletScaContext } from './hoc/withWalletScaContext';
import { useShallow } from 'zustand/react/shallow';
import { useTranslation } from '@contexts/translation/hooks/useTranslation';
import { ScaPanelCmsWithDefault } from './ScaPanelCmsWithDefault';
import { walletLogoUrl } from '@v2/utils/constants';
import { useWebAnalyticBinder } from '@components/panels/SignupPanel/useWebAnalyticBinder';
import { ScaPageBinder } from './analytics';

const ScaPanel: React.FC = () => {
    useWebAnalyticBinder(ScaPageBinder);
    const { sdkEffect, logger } = useServiceResolver();
    const { setBackgroundColor } = usePageBackground();

    const { walletStorage } = useWalletContext();
    const { walletAuthState, setTransactionData, setWalletAuthState } = useStore(
        walletStorage,
        useShallow(state => ({
            walletAuthState: state.walletAuthState,
            setTransactionData: state.setTransactionData,
            setWalletAuthState: state.setWalletAuthState,
        }))
    );

    const renderNotification = useRenderNotification('WalletSca');
    const pushNotification = usePushNotification('WalletSca');

    useSetNavHeader('Agoda Wallet', sdkEffect.onExit, false, walletLogoUrl);
    setBackgroundColor('base.background');

    const translation = useTranslation(ScaPanelCmsWithDefault);

    // To be updated with CMS
    const pinFormProps: PinFormProps = {
        cms: {
            initial: {
                title: translation.ConfirmPinTitle,
                caption: translation.ConfirmPinCaption,
            },
        },
        currentPath: 'walletSca',
        showForgotPin: true,
    };

    const renderForm = () => {
        switch (walletAuthState) {
            case 'loading':
                return renderLoadingForm();
            case 'otp':
                return <WalletOtpForm />;
            case 'pin':
                return <PinForm {...pinFormProps} />;
        }
    };

    const renderLoadingForm = () => (
        <Box data-cy="wallet-sca-loading-container" width="100%" p="16" display="flex" justifyContent="center">
            <DotsLoader variant="brand" />
        </Box>
    );

    const getTransactionData = async () => {
        try {
            const response = await sdkEffect.onTransactionWillLoad();
            setTransactionData(response);
            setWalletAuthState('pin');
        } catch (error) {
            logger.error('RPC onTransactionWillLoad error', error);
            pushNotification({
                color: 'error',
                message: translation.TechnicalDifficulty,
                testId: 'technical-difficulty',
            });
        }
    };

    React.useEffect(() => {
        getTransactionData();
    }, []);

    return (
        <>
            {renderNotification()}
            <WalletCard data-cy="wallet-auth-panel" p="12">
                {renderForm()}
            </WalletCard>
        </>
    );
};

const WalletScaPanel = WithWalletScaContext(ScaPanel);

export { WalletScaPanel };
